'use es6';

import PropTypes from 'prop-types';
import { Component } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import Raven from 'raven-js';
import styled from 'styled-components';
import UIIconCircle from 'UIComponents/icon/UIIconCircle';
import UILink from 'UIComponents/link/UILink';
import UIModal from 'UIComponents/dialog/UIModal';
import UIFlex from 'UIComponents/layout/UIFlex';
import FormattedMessage from 'I18n/components/FormattedMessage';
import WistiaVideoPlayer from 'ui-addon-video-player/components/WistiaVideoPlayer';
import H2 from 'UIComponents/elements/headings/H2';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import SkeletonText from 'conversations-skeleton-state/components/SkeletonText';
import { parseUrl } from 'hub-http/helpers/url';
import { GRID_BREAKPOINT_SMALL } from 'HubStyleTokens/sizes';
import { startConfetti } from '../../../utils/confetti';
import TrackAwardModalSharing from './TrackAwardModalSharing';
import { getAwardSharingPath } from '../../../utils/RoutingUtils';
import { ConnectDotComUpsell } from '../../../features/connectDotCom';
import { CertificateImage } from '../../../features/achievementImages';
import certBackgroundShapeUrl from 'bender-url!../../../../images/blob.svg';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BASE_CLASS_NAME = 'track-award-modal';
const CONFETTI_TIME = 600000;
const SubHeadingSkeletonContainer = styled.div.withConfig({
  displayName: "TrackAwardModal__SubHeadingSkeletonContainer",
  componentId: "s9e0bt-0"
})(["margin:auto;width:50%;margin-bottom:16px !important;"]);
const CertificateBackground = styled(UIFlex).withConfig({
  displayName: "TrackAwardModal__CertificateBackground",
  componentId: "s9e0bt-1"
})(["background-image:url(", ");background-position:top;background-repeat:no-repeat;background-size:contain;z-index:-1;width:100%;max-width:750px;padding:10px;margin:0;@media (min-width:", "){padding:30px 64px 64px;margin:-30px 0 -100px;}"], props => props.src, GRID_BREAKPOINT_SMALL);
const CertificateContainer = styled.div.withConfig({
  displayName: "TrackAwardModal__CertificateContainer",
  componentId: "s9e0bt-2"
})(["display:grid;grid-template-columns:1fr;@media (min-width:", "){grid-template-columns:1fr 1fr;}"], GRID_BREAKPOINT_SMALL);
const ConnectDotComUpsellContainer = styled.div.withConfig({
  displayName: "TrackAwardModal__ConnectDotComUpsellContainer",
  componentId: "s9e0bt-3"
})(["max-width:320px;margin-bottom:20px;@media (min-width:", "){margin:44px auto 0;}"], GRID_BREAKPOINT_SMALL);
class TrackAwardModal extends Component {
  constructor(...args) {
    super(...args);
    this.state = {};
    this.onDownloadCertificate = event => {
      if (window.navigator.msSaveBlob && this.certImageBlobForIE) {
        window.navigator.msSaveBlob(this.certImageBlobForIE, event.currentTarget.getAttribute('download'));
      }
      this.props.onDownloadCertificate(event);
    };
    this.onImgLoadError = ({
      currentTarget
    }) => {
      const {
        award
      } = this.props;
      const {
        hasRetriedLoading
      } = this.state;
      const extra = {
        category: currentTarget.dataset.category,
        src: currentTarget.src,
        award
      };
      this.captureBreadcrumb('onImgLoadError');

      // The award is set to visible, but there was an error loading the s3 image.
      // Generate public urls and log to Sentry
      if (award.visibility === 'PUBLIC' && !hasRetriedLoading) {
        this.setState({
          hasRetriedLoading: true
        });
        Raven.captureMessage('TrackAwardModal: Had to get signed s3 urls for a supposedly visible award', {
          extra
        });
      }

      // Image load failed despite using signed urls
      if (!(award.visibility === 'PUBLIC' && !hasRetriedLoading)) {
        Raven.captureMessage('TrackAwardModal: Error loading image from s3', {
          extra
        });
      }
    };
    this.onLoadCertImage = ({
      currentTarget
    }) => {
      if (!this.state.certObjectUrl) {
        const cnv = document.createElement('canvas');
        cnv.height = currentTarget.naturalHeight;
        cnv.width = currentTarget.naturalWidth;
        cnv.getContext('2d').drawImage(currentTarget, 0, 0);
        if (cnv.toBlob) {
          cnv.toBlob(blob => {
            this.setCertImageState(blob, currentTarget.src);
          });
        } else if (cnv.msToBlob) {
          // IE fallback
          setTimeout(() => {
            // Delayed to allow canvas.drawImage to fully complete
            this.certImageBlobForIE = cnv.msToBlob();
            this.setCertImageState(this.certImageBlobForIE, currentTarget.src);
          });
        }
        this.captureBreadcrumb('onLoadCertImage', {
          src: currentTarget.src,
          height: cnv.height,
          width: cnv.width
        });
      }
    };
    this.onTogglePublicVisibility = checked => {
      this.captureBreadcrumb('onTogglePublicVisibility', {
        checked
      });
    };
  }
  componentDidMount() {
    const {
      award,
      onMount
    } = this.props;
    if (new Date().getTime() - award.awardedAt < CONFETTI_TIME) {
      this.cleanupConfetti = startConfetti();
    }
    onMount();
    this.captureBreadcrumb('componentDidMount');
  }
  componentDidUpdate(prevProps) {
    if (prevProps.award !== this.props.award) {
      this.captureBreadcrumb('componentDidUpdate (changed award)', {
        prevAward: prevProps.award
      });
    }
  }
  componentWillUnmount() {
    URL.revokeObjectURL(this.state.certObjectUrl);
    if (this.cleanupConfetti) {
      this.cleanupConfetti();
      delete this.cleanupConfetti;
    }
    this.captureBreadcrumb('componentWillUnmount');
  }
  captureBreadcrumb(message, data = {}) {
    const {
      award
    } = this.props;
    Raven.captureBreadcrumb({
      message,
      category: 'TrackAwardModal',
      data: Object.assign({
        award
      }, data)
    });
  }
  setCertImageState(blob, src) {
    this.setState({
      certObjectUrl: URL.createObjectURL(blob),
      certFileName: parseUrl(src).path.replace(/^.*\//, '')
    });
  }
  render() {
    const {
      award,
      examAttempt,
      firstName,
      onClickShareThis,
      onClose,
      track
    } = this.props;
    const endOfTrackCtaText = track.get('endOfTrackCtaText');
    const endOfTrackCtaLink = track.get('endOfTrackCtaLink');
    const passVideo = track.getIn(['passVideo', 'url']);
    if (!examAttempt) return null;
    const correctQuestionCount = examAttempt.score;
    const totalQuestionCount = examAttempt.questionIds && examAttempt.questionIds.length || 0;
    const isSubheadingReady = track && track.get('name');
    return /*#__PURE__*/_jsxs(UIModal, {
      "data-test-id": "track-award-modal",
      use: "conversational",
      width: 1250,
      children: [/*#__PURE__*/_jsx(UIDialogHeader, {
        children: /*#__PURE__*/_jsx(H2, {
          "aria-level": 1,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.exams.finished.successHeading",
            options: {
              firstName
            }
          })
        })
      }), /*#__PURE__*/_jsx(UIIconCircle, {
        className: `${BASE_CLASS_NAME}__close-icon`,
        name: "remove",
        onClick: onClose,
        size: 20
      }), /*#__PURE__*/_jsxs("div", {
        className: `${BASE_CLASS_NAME}`,
        children: [/*#__PURE__*/_jsxs("div", {
          className: `${BASE_CLASS_NAME}__header-container`,
          children: [isSubheadingReady ? /*#__PURE__*/_jsxs("p", {
            className: `${BASE_CLASS_NAME}__header-text`,
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.exams.finished.passedSubheading",
              options: {
                courseName: track.get('name')
              }
            }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.exams.finished.yourScoreWas",
              options: {
                correctQuestionCount,
                totalQuestionCount
              }
            })]
          }) : /*#__PURE__*/_jsxs(SubHeadingSkeletonContainer, {
            children: [/*#__PURE__*/_jsx(SkeletonText, {}), /*#__PURE__*/_jsx(SkeletonText, {})]
          }), !!endOfTrackCtaText && !!endOfTrackCtaLink && /*#__PURE__*/_jsx(UILink, {
            href: endOfTrackCtaLink,
            target: "_blank",
            children: /*#__PURE__*/_jsx("h3", {
              children: endOfTrackCtaText
            })
          })]
        }), /*#__PURE__*/_jsx("div", {
          className: `${BASE_CLASS_NAME}__body-container`,
          children: /*#__PURE__*/_jsxs(CertificateContainer, {
            children: [/*#__PURE__*/_jsx("div", {
              children: /*#__PURE__*/_jsxs(CertificateBackground, {
                align: "center",
                direction: "column",
                src: certBackgroundShapeUrl,
                children: [/*#__PURE__*/_jsx(CertificateImage, {
                  onError: this.onImgLoadError,
                  onLoad: this.onLoadCertImage,
                  trackId: track.get('id')
                }), !!passVideo && /*#__PURE__*/_jsx("div", {
                  className: `${BASE_CLASS_NAME}__pass-video`,
                  children: /*#__PURE__*/_jsx(WistiaVideoPlayer, {
                    videoId: passVideo
                  })
                }), /*#__PURE__*/_jsx(TrackAwardModalSharing, {
                  certFileName: this.state.certFileName,
                  certObjectUrl: this.state.certObjectUrl,
                  onClickShareThis: onClickShareThis,
                  onDownloadCertificate: this.onDownloadCertificate,
                  shareThisPath: getAwardSharingPath(track.get('id'))
                })]
              })
            }), /*#__PURE__*/_jsx(ConnectDotComUpsellContainer, {
              children: /*#__PURE__*/_jsx(ConnectDotComUpsell, {
                onClickJoinConnectDotCom: this.props.onClickJoinConnectDotCom,
                onClickViewConnectDotCom: this.props.onClickViewConnectDotCom,
                userId: award.userId
              })
            })]
          })
        })]
      })]
    });
  }
}
TrackAwardModal.propTypes = {
  award: PropTypes.object.isRequired,
  examAttempt: PropTypes.object,
  firstName: PropTypes.string,
  onClickJoinConnectDotCom: PropTypes.func.isRequired,
  onClickShareThis: PropTypes.func.isRequired,
  onClickViewConnectDotCom: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onDownloadCertificate: PropTypes.func,
  onMount: PropTypes.func.isRequired,
  track: ImmutablePropTypes.map.isRequired
};
TrackAwardModal.defaultProps = {
  examAttempt: {
    score: 0
  },
  hasAwardPrivacyToggle: false,
  onClose: () => {},
  onDownloadCertificate: () => {},
  onMount: () => {}
};
export default TrackAwardModal;